import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlugCircleBolt,
    faLocationDot,
    faChargingStation,
    faPlug,
    faUser,
    faAddressCard,
    faClock,
} from "@fortawesome/pro-regular-svg-icons";
import ModalBase from "../ModalBase";
import ChargingMetricCurve from "./ChargingMetricCurve";
import ChargeMonitorActions from "./ChargeMonitorActions";
import { ModalInfoList, ModalInfoRow } from "./ModalInfoList";
import {
    StatusIconByKey,
    getStatusLabelWithMaintenance,
    getStatusColorStyle,
    getStatusChipStyleVars,
    getStatusClasses,
} from "../../helpers/StatusMapper";
import { formatSwedishTime } from "../../helpers/DateTime";
import { SESSION_STATUSES } from "./chargeMonitorHelpers";

const ChargeMonitorConnectorModal = ({
    item,
    isOpen,
    onClose,
    canManage,
    onAction,
}) => {
    const { t } = useTranslation();

    if (!item) {
        return null;
    }

    const data = item.Data || {};
    const status = (data.Status || data.chargingConnectorStatus || "").toUpperCase();
    const maintenanceMode = !!(data.maintenanceMode ?? data.MaintenanceMode);
    const visualStatus = maintenanceMode ? "UNAVAILABLE" : status;
    const isOnline = !!data.isOnline;
    const isSession = SESSION_STATUSES.includes(status);
    const statusColorStyle = getStatusColorStyle(visualStatus);
    const siteName = data.ChargingSiteName || "–";
    const chargerName = data.chargingPointName || "–";
    const connectorName = data.chargingConnectorName || "–";
    const statusLabel = getStatusLabelWithMaintenance(status, maintenanceMode, t);

    return (
        <ModalBase isOpen={isOpen} onClose={onClose} size={isSession ? "xl" : "lg"} zIndex={900}>
            <div
                className={`flex min-h-0 flex-col ${
                    isSession
                        ? "h-full overflow-hidden"
                        : "max-h-[calc(100dvh-6rem)] overflow-y-auto md:overflow-hidden"
                }`}
            >
                <div
                    className={
                        isSession
                            ? "min-h-0 flex-1 overflow-y-auto pr-1"
                            : "min-h-0 pr-1 md:flex-1 md:overflow-y-auto"
                    }
                >
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 pr-10">
                        <h2 className="flex min-w-0 items-center gap-2 text-xl font-semibold text-sky-600">
                            <FontAwesomeIcon icon={faPlugCircleBolt} className="shrink-0 text-lg text-sky-500" />
                            <span className="truncate">{chargerName}</span>
                        </h2>
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span
                                className={getStatusClasses(visualStatus)}
                                style={getStatusChipStyleVars(visualStatus)}
                            >
                                {statusLabel}
                            </span>
                            <span
                                className={getStatusClasses(isOnline ? "ONLINE" : "OFFLINE")}
                                style={getStatusChipStyleVars(isOnline ? "ONLINE" : "OFFLINE")}
                            >
                                {isOnline ? t("common.online") : t("common.offline")}
                            </span>
                        </div>
                    </div>

                    <ModalInfoList withIcons className="mb-4 border-b border-mute-100 pb-4">
                        <ModalInfoRow icon={faLocationDot} iconClass="text-sky-500" label={t("entity.site")}>
                            {siteName}
                        </ModalInfoRow>
                        <ModalInfoRow icon={faChargingStation} iconClass="text-kraft-500" label={t("entity.charger")}>
                            {chargerName}
                        </ModalInfoRow>
                        <ModalInfoRow icon={faPlug} label={t("entity.connector")}>
                            {connectorName}
                        </ModalInfoRow>
                        <ModalInfoRow
                            icon={StatusIconByKey[visualStatus] ?? StatusIconByKey.DEFAULT}
                            iconClass=""
                            iconStyle={statusColorStyle}
                            label={t("common.status")}
                            valueStyle={statusColorStyle}
                        >
                            {statusLabel}
                        </ModalInfoRow>
                        {isSession && data.SessionCustomerName && (
                            <ModalInfoRow icon={faUser} label={t("entity.customer")}>
                                {data.SessionCustomerName}
                            </ModalInfoRow>
                        )}
                        {isSession && data.SessionTokenName && (
                            <ModalInfoRow icon={faAddressCard} label={t("entity.token")}>
                                {data.SessionTokenName}
                            </ModalInfoRow>
                        )}
                        {isSession && data.SessionStartTime && (
                            <ModalInfoRow icon={faClock} label={t("chargeMonitor.started")}>
                                {formatSwedishTime(data.SessionStartTime)}
                            </ModalInfoRow>
                        )}
                        {status === "RESERVED" && data.CurrentReservationExpiryTime && (
                            <ModalInfoRow icon={faClock} label={t("chargeMonitor.reservedUntil")}>
                                {formatSwedishTime(data.CurrentReservationExpiryTime)}
                            </ModalInfoRow>
                        )}
                    </ModalInfoList>

                    {isSession && (
                        <div className="pb-4">
                            <ChargingMetricCurve
                                isOpen
                                connectorPublicId={data.chargingConnectorPublicId}
                                useSse={true}
                                animate={false}
                            />
                        </div>
                    )}
                </div>

                <div
                    className={`border-t border-mute-200 bg-mute-25 pt-4 ${
                        isSession ? "shrink-0" : "md:shrink-0"
                    }`}
                >
                    <ChargeMonitorActions
                        item={item}
                        canManage={canManage}
                        onAction={onAction}
                        layout="footer"
                    />
                </div>
            </div>
        </ModalBase>
    );
};

export default ChargeMonitorConnectorModal;
